import { useMemo, useState } from 'react'

import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingLifecycleAction } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { invalidateBlockApprovalRequestQueries } from '~/hooks/bidding/queryKeys'
import { useTransitionBiddingBlockApprovalState } from '~/hooks/bidding/useTransitionBiddingBlockApprovalState'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { now as serverNow } from '~/models/clock'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import { useBlockBidLimits } from '~/pages/SpotBidding/hooks/useBlockBidLimits'
import { buildApproverConfirmNotices } from '~/pages/SpotBidding/models/approverIdentity'
import { buildBlockApprovalChecks } from '~/pages/SpotBidding/models/blockApprovalViolation'
import {
  BlockDetailUnresolvedError,
  translateBlockWriteError,
} from '~/pages/SpotBidding/models/blockBidWrite'
import { countBlockGroupLegs } from '~/pages/SpotBidding/models/blockGroupLegs'
import type { SpotMarketPhase } from '~/pages/SpotBidding/models/marketPhase'
import {
  calcSpotPhase,
  isSpotBeforeDeadline,
} from '~/pages/SpotBidding/models/marketPhase'
import type { VolumeCountableBid } from '~/pages/SpotBidding/models/spotBidValidation'

type Params = {
  /**
   * PENDING かつ明細 Get 解決済みの group (= 確定時の対象解決元)。 id ではなく確定時に
   * ここから引き直すことで、 modal を開いたまま背景 refetch が走っても stale な
   * `expectedUpdatedAt` を握らない。
   */
  pendingGroups: BiddingBlockApprovalRequest[]
  /** 一覧 / 明細 Get が未解決か (= 中身を確認できないまま承認させない fail-closed)。 */
  isStateUnresolved: boolean
  /** 完了済み / 削除済みワークフロー経由か (AC-BSP-058)。 */
  isReadonly: boolean
  /**
   * 導線の出し分けに使う市場フェーズ (= 分単位 tick 由来、 締切 AC-BSP-099)。 確定時は
   * これではなく `targetDate` とサーバー由来の現在時刻で引き直す。
   */
  phase: SpotMarketPhase
  /** 受渡日 (= 締切 AC-BSP-099 の再判定に使う)。 */
  targetDate: Date
  /** 遷移の直前に呼ぶ (= ブロックタブの書込エラー表示のクリア)。 */
  onTransitionStart?: () => void
  /**
   * 上限数量(コマ計) の再検証母集合 (AC-BSP-097 / AC-BSP-080)。 受渡日内の有効な通常入札と、
   * 有効なブロック入札の当該コマ数量を足したもの (page が組む)。 承認対象自身 (PENDING) も
   * ブロック側に含めて渡す (サーバーも自 group の leg + 他 group の合算で判定する)。
   */
  volumeTotalRows: readonly VolumeCountableBid[]
  /**
   * 通常入札の一覧 / 明細が未解決か (= `volumeTotalRows` の通常入札分を全量と確定できない)。 上限数量(コマ計)
   * の再検証が「見えている通常入札の範囲での違反なし」 に縮退するため、 上限数量(コマ計) が
   * 効くコマでは fail-closed にする。
   */
  isSpotVolumeUnresolved: boolean
}

export type BlockApprovalDecision = {
  /** 承認確認 modal の対象 group id (= undefined で非表示)。 */
  approvingGroupId: string | undefined
  /** 差戻しダイアログの対象 group id (= undefined で非表示)。 */
  rejectingGroupId: string | undefined
  openApprove: (group: BiddingBlockApprovalRequest) => void
  openReject: (group: BiddingBlockApprovalRequest) => void
  closeApprove: () => void
  closeReject: () => void
  approve: () => Promise<void>
  reject: (reason: string) => Promise<void>
  isTransitioning: boolean
  /**
   * 承認 / 差戻し を今できない理由 (= バナーのボタンを disabled + tooltip にする文言)。
   * undefined なら操作可。 確定時ガードと同じ policy を導線側にも出す唯一の口
   * (通常入札の `useSpotApprovalDecision` と同型)。
   */
  decisionDisabledReason: string | undefined
  /**
   * 承認 / 差戻し を `phaseAt` の時点でできない理由 (= `decisionDisabledReason` と同じ policy)。
   * まとめて承認 (AC-BSP-118) が確定時にサーバー由来の現在時刻で引き直すのに使い、 可否 policy を
   * 単件と分けて持たない。
   */
  decisionBlockedReasonAt: (phaseAt: SpotMarketPhase) => string | undefined
  /**
   * 承認確認 modal の件数行に出す leg (札) 数。 承認単位は BlockBidGroup 1 件だが
   * LINK / LOOP は leg を 2 本持つため「1 件」 では対象を誤認する。
   */
  legCount: number
  /** 承認確認 modal に出す承認者でない・自己承認の再確認 (AC-BSP-098。 確定は止めない)。 */
  approveWarningMessages: string[]
  /** 差戻しダイアログに出す承認者でない・自己承認の再確認 (AC-BSP-098)。 */
  rejectWarningMessages: string[]
  /** 承認確認 modal に積む違反明細 (AC-BSP-097 の再検証結果)。 */
  violationSections: ViolationSection[]
  /** 違反があるか (= 承認確定ボタンを非活性にする、 AC-BSP-097)。 */
  hasViolation: boolean
}

/**
 * ブロック入札の承認 / 差戻し (PENDING → APPROVED / REJECTED、 AC-BSP-094) の状態と操作。
 *
 * 遷移単位は BlockBidGroup 単位 (AC-BSP-059: グループ内の各レグは同じ承認状態を投影する
 * ため、 レグ単位の承認は取り得ない)。 通常入札の `useSpotApprovalDecision` と対で、
 * page (`SpotBidding/index.tsx`) から同じ理由で切り出している。
 *
 * 承認者でない人・申請者本人も承認 / 差戻しでき、 確認 modal で再確認する (AC-BSP-098)。
 */
export const useBlockApprovalDecision = ({
  pendingGroups,
  isStateUnresolved,
  isReadonly,
  phase,
  targetDate,
  onTransitionStart,
  volumeTotalRows,
  isSpotVolumeUnresolved,
}: Params): BlockApprovalDecision => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { mutateAsync: transitionAsync, isPending: isTransitioning } =
    useTransitionBiddingBlockApprovalState()
  // 入札制限は取得中も空 lookup で返り「制限未設定」 と区別できない (`useBidLimits` の
  // JSDoc) ため、 取得中または取得失敗の間は再検証が成立しないものとして fail-closed にする。
  // 取り直し中は取り直す前の値が残るので、 初回の取得中 (isLoading) に限らず isFetching で見る。
  const {
    limits,
    isFetching: isLimitsFetching,
    isError: isLimitsError,
    refetch: refetchLimits,
  } = useBlockBidLimits()

  // entity ではなく id で保持する (= modal を開いたまま invalidate/refetch が走っても
  // stale な expectedUpdatedAt を握り続けないため。 申請取消 cancelingBlockGroupId と
  // 同じ判断)。
  const [approvingGroupId, setApprovingGroupId] = useState<string | undefined>(
    undefined,
  )
  const [rejectingGroupId, setRejectingGroupId] = useState<string | undefined>(
    undefined,
  )

  const approvingGroup = useMemo(
    () => pendingGroups.find((g) => g.id === approvingGroupId),
    [approvingGroupId, pendingGroups],
  )
  const legCount = useMemo(
    () => countBlockGroupLegs(approvingGroup),
    [approvingGroup],
  )
  // AC-BSP-097: 承認時点の入札制限で再検証する。 サーバーは承認時に同じ軸 (価格の上下限・
  // 上限数量(1札)・上限数量(コマ計)) で拒否する (BlockBidUsecase.ensureWithinBidLimitsAtTransition)
  // ので、 承認者が確定を押す前に違反の内容を見せて確定を止める。 承認者は札を直せないので、
  // 救済は「差戻し」 (通常入札の useSpotApprovalDecision と同じ)。 承認待ちの間に入札制限が
  // 変わっても承認時点の値で判定できるよう、 modal を開く時に入札制限を取り直す (openApprove)。
  const violationSections = useMemo(() => {
    if (approvingGroup === undefined) {
      return []
    }
    const checks = buildBlockApprovalChecks(
      approvingGroup,
      {
        limits,
        volumeTotalRows,
        isLimitsUnavailable: isLimitsFetching || isLimitsError,
        isSpotVolumeUnresolved,
      },
      t,
    )
    // 単件は判定できない節も違反と同じく確定を止める。
    return [...checks.violationSections, ...checks.unverifiedSections]
  }, [
    approvingGroup,
    isLimitsError,
    isLimitsFetching,
    isSpotVolumeUnresolved,
    limits,
    t,
    volumeTotalRows,
  ])
  const hasViolation = violationSections.some((s) => s.details.length > 0)

  const approverIdentity = useApproverIdentity()
  const warningMessagesFor = (
    groupId: string | undefined,
    action: 'approve' | 'reject',
  ): string[] => {
    const group = pendingGroups.find((g) => g.id === groupId)
    return group === undefined
      ? []
      : buildApproverConfirmNotices(approverIdentity(group), action, t)
  }

  /**
   * 承認 / 差戻し の可否 policy (AC-BSP-058 の確定済ワークフロー / AC-BSP-099 の締切 /
   * 明細未解決)。 導線側は `decisionDisabledReason` (= バナーの disabled + tooltip)、
   * 確定時は transition() が同じ関数をサーバー由来の現在時刻で引き直す。 ガードの取捨は
   * 申請取消 (handleCancelBlockRequest) に揃えている。
   *
   * - isReadonly: 導線の無効化だけでは、 modal を開いたまま別ユーザーがケースを
   *   完了させた / ケース取得が失敗して isReadonly に倒れた場合に遷移が飛んでしまう。
   *   backend も case_id でケース状態を検証する (AC-BSP-058) が、 確定時に送る前に
   *   画面側でも止める (導線と同じ fail-closed)
   * - 締切 (前日 10:00): 導線に使う phase は分単位 tick 由来で最大 1 分古いため、 確定時は
   *   サーバー由来の現在時刻で引き直す (申請取消 / 再編集の締切ガードと同じ形)
   * - 明細 Get が未解決の group が残る間は、 その内容を承認者が確認できていない。 中身を
   *   確認できないまま承認 (= JEPX 提出可能状態への遷移) を実行する事故を防ぐため
   *   fail-closed
   */
  const blockedReasonFor = (phaseAt: SpotMarketPhase): string | undefined => {
    if (isReadonly) {
      // undefined は「できる」 を意味するので、 取得中・取得失敗の fail-closed 中も同じ文で塞ぐ。
      return t('common:workflowLock.readonlyReason')
    }
    if (!isSpotBeforeDeadline(phaseAt)) {
      return t('page:biddingBlock.approvalAction.decisionDeadlinePassed')
    }
    if (isStateUnresolved) {
      return translateBlockWriteError(new BlockDetailUnresolvedError(), t)
    }
    return undefined
  }
  const decisionDisabledReason = blockedReasonFor(phase)

  /**
   * 承認 / 差戻し の共通処理 (= 上の policy で確定時に再判定し、 対象を解決して遷移)。
   */
  const transition = async (
    targetId: string | undefined,
    action:
      | typeof BiddingLifecycleAction.APPROVE
      | typeof BiddingLifecycleAction.REJECT,
    reason: string | undefined,
    successKey: string,
    clearTarget: () => void,
  ): Promise<void> => {
    if (targetId === undefined) return
    const abort = (title: string): void => {
      showErrorToast({ title })
      clearTarget()
    }
    const blockedReason = blockedReasonFor(
      calcSpotPhase(serverNow(), targetDate),
    )
    if (blockedReason !== undefined) {
      abort(blockedReason)
      return
    }
    // 背景再取得で対象が PENDING でなくなっていた (= 他ユーザーが承認/差戻し/取消済み)
    // 場合は中断する。
    const target = pendingGroups.find((g) => g.id === targetId)
    if (target === undefined) {
      abort(t('page:biddingBlock.serverError.aborted'))
      return
    }
    onTransitionStart?.()
    try {
      await transitionAsync({
        id: target.id,
        action,
        reason,
        expectedUpdatedAt: target.updatedAt,
      })
      showSuccessToast({ title: t(successKey) })
      clearTarget()
    } catch (err) {
      // 楽観ロック競合 (Aborted) 等で失敗した場合、 一覧・明細を invalidate して実状態を
      // 再取得する (= handleCancelBlockRequest の catch と対称)。 サーバーが入札制限の違反で
      // 拒否した場合に modal が違反の内容を出せるよう、 入札制限も取り直す。
      void invalidateBlockApprovalRequestQueries(queryClient)
      void refetchLimits()
      showErrorToast({ title: translateBlockWriteError(err, t) })
    }
  }

  return {
    approvingGroupId,
    rejectingGroupId,
    openApprove: (group) => {
      onTransitionStart?.()
      void refetchLimits()
      setApprovingGroupId(group.id)
    },
    openReject: (group) => {
      onTransitionStart?.()
      setRejectingGroupId(group.id)
    },
    closeApprove: () => {
      setApprovingGroupId(undefined)
    },
    closeReject: () => {
      setRejectingGroupId(undefined)
    },
    approve: () =>
      transition(
        approvingGroupId,
        BiddingLifecycleAction.APPROVE,
        undefined,
        'page:biddingBlock.approvalAction.approveSuccess',
        () => {
          setApprovingGroupId(undefined)
        },
      ),
    // 差戻し理由は必須 (= 通常入札と同じ判断。 AC-BSP-063 / backend の非空検証)。
    reject: (reason: string) =>
      transition(
        rejectingGroupId,
        BiddingLifecycleAction.REJECT,
        reason,
        'page:biddingBlock.approvalAction.rejectSuccess',
        () => {
          setRejectingGroupId(undefined)
        },
      ),
    isTransitioning,
    decisionDisabledReason,
    decisionBlockedReasonAt: blockedReasonFor,
    legCount,
    approveWarningMessages: warningMessagesFor(approvingGroupId, 'approve'),
    rejectWarningMessages: warningMessagesFor(rejectingGroupId, 'reject'),
    violationSections,
    hasViolation,
  }
}
